// FleetTable — sortable vehicle overview table with color-coded age/status

import { useState } from "react";

export interface FleetRow {
  unit: string;
  vehicleType: string;
  assignment: string;
  year: number;
  status: string;
  yearsInService: number;
  maxAge?: number;
}

export interface FleetTableProps {
  vehicles: FleetRow[];
  isLoading?: boolean;
}

type SortKey =
  | "unit"
  | "vehicleType"
  | "assignment"
  | "year"
  | "status"
  | "yearsInService";
type SortDir = "asc" | "desc";

const DEFAULT_MAX_AGE = 20;

function getRowAgeVariant(row: FleetRow): "red" | "yellow" | "green" {
  const maxAge = row.maxAge ?? DEFAULT_MAX_AGE;
  if (row.yearsInService > maxAge) return "red";
  if (row.yearsInService > maxAge * 0.8) return "yellow";
  return "green";
}

const ROW_AGE_CLASSES: Record<"red" | "yellow" | "green", string> = {
  red: "border-l-4 border-l-red-500 bg-red-500/5",
  yellow: "border-l-4 border-l-amber-400 bg-amber-400/5",
  green: "border-l-4 border-l-transparent bg-transparent",
};

function getStatusBadge(status: string): { bg: string; dot: string } {
  const normalized = status.toLowerCase().replace(/[-_\s]/g, "");
  if (normalized === "inservice") {
    return {
      bg: "bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30",
      dot: "bg-green-500",
    };
  }
  if (normalized === "outofservice") {
    return {
      bg: "bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30",
      dot: "bg-red-500",
    };
  }
  // Fallback — amber for any other status (e.g. "Reserve", "Shop")
  return {
    bg: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30",
    dot: "bg-amber-500",
  };
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <span
      className={`inline-flex flex-col ml-1 opacity-${active ? "100" : "40"}`}
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={
          active && dir === "asc" ? "text-brand-adaptive" : "text-theme-muted"
        }
      >
        <polyline points="18 15 12 9 6 15" />
      </svg>
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={
          active && dir === "desc" ? "text-brand-adaptive" : "text-theme-muted"
        }
        style={{ marginTop: -3 }}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </span>
  );
}

function sortVehicles(
  vehicles: FleetRow[],
  key: SortKey,
  dir: SortDir,
): FleetRow[] {
  return [...vehicles].sort((a, b) => {
    let cmp = 0;
    if (key === "year" || key === "yearsInService") {
      cmp = (a[key] as number) - (b[key] as number);
    } else {
      cmp = String(a[key]).localeCompare(String(b[key]));
    }
    return dir === "asc" ? cmp : -cmp;
  });
}

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "unit", label: "Unit" },
  { key: "vehicleType", label: "Type" },
  { key: "assignment", label: "Assignment" },
  { key: "year", label: "Year" },
  { key: "status", label: "Status" },
  { key: "yearsInService", label: "Years in Service" },
];

export default function FleetTable({
  vehicles,
  isLoading = false,
}: FleetTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("unit");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  if (isLoading) {
    return (
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b border-theme-subtle">
          <div className="skeleton h-4 w-32 rounded-md" />
        </div>
        <div className="p-4 space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton h-10 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const sorted = sortVehicles(vehicles, sortKey, sortDir);

  return (
    <div className="glass rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 border-b border-theme-subtle flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-theme-primary leading-tight">
          Fleet Overview
        </h2>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-theme-overlay text-theme-muted">
          {vehicles.length} unit{vehicles.length !== 1 ? "s" : ""}
        </span>
      </div>

      {vehicles.length === 0 ? (
        <div className="py-10 text-center">
          <p className="text-sm text-theme-muted">No fleet data available</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-theme-subtle">
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="px-4 py-2.5 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider cursor-pointer select-none hover:text-theme-primary transition-colors whitespace-nowrap"
                  >
                    <span className="inline-flex items-center">
                      {col.label}
                      <SortIcon active={sortKey === col.key} dir={sortDir} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((vehicle) => {
                const ageVariant = getRowAgeVariant(vehicle);
                const statusBadge = getStatusBadge(vehicle.status);
                const maxAge = vehicle.maxAge ?? DEFAULT_MAX_AGE;

                return (
                  <tr
                    key={vehicle.unit}
                    className={`transition-colors hover:bg-theme-overlay/50 ${ROW_AGE_CLASSES[ageVariant]}`}
                  >
                    <td className="px-4 py-2.5 font-semibold text-theme-primary whitespace-nowrap">
                      {vehicle.unit}
                    </td>
                    <td className="px-4 py-2.5 text-theme-secondary whitespace-nowrap">
                      {vehicle.vehicleType}
                    </td>
                    <td className="px-4 py-2.5 text-theme-secondary whitespace-nowrap">
                      {vehicle.assignment}
                    </td>
                    <td className="px-4 py-2.5 text-theme-secondary tabular-nums">
                      {vehicle.year}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.bg}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusBadge.dot}`}
                        />
                        {vehicle.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">
                      <span
                        className={`font-semibold ${
                          ageVariant === "red"
                            ? "text-status-red"
                            : ageVariant === "yellow"
                              ? "text-status-amber"
                              : "text-status-green"
                        }`}
                      >
                        {vehicle.yearsInService}
                      </span>
                      <span className="text-theme-muted text-xs ml-1">
                        / {maxAge} yr max
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Age legend */}
      <div className="px-5 py-3 border-t border-theme-subtle flex items-center gap-4">
        <span className="text-xs text-theme-muted font-medium">Age:</span>
        {[
          { color: "bg-status-green", label: "Within service life" },
          { color: "bg-status-amber", label: ">80% of max age" },
          { color: "bg-status-red", label: "Exceeded max age" },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
            <span className="text-xs text-theme-muted">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
